import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Badge, Spinner } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSchool, faBook, faChalkboardTeacher, faUserGraduate } from '@fortawesome/free-solid-svg-icons';
import { useSchool } from '../../contexts/SchoolContext';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import DashboardService from '../../services/dashboard.service';

const TeacherDashboard = ({ user }) => {
  const { selectedSchool, availableSchools, getSelectedSchoolData } = useSchool();
  const { selectedYear } = useAcademicYear();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    classes: 0,
    subjects: 0,
    students: 0,
    loadingStats: true
  });

  useEffect(() => {
    if (user?.school_users) {
      const teacherSchools = user.school_users.filter(
        su => su.role === 'TEACHER' && su.active
      );
      setSchools(teacherSchools);
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (selectedSchool && selectedYear && user?.id) {
      fetchTeacherStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchool, selectedYear, user]);

  const fetchTeacherStats = async () => {
    try {
      setStats(prev => ({ ...prev, loadingStats: true }));

      // Buscar estatísticas do dashboard
      const response = await DashboardService.getStats({
        school: selectedSchool,
        year: selectedYear
      });

      setStats({
        classes: response.stats.classes || 0,
        subjects: response.stats.subjects || 0,
        students: response.stats.students || 0,
        loadingStats: false
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setStats({
        classes: 0,
        subjects: 0,
        students: 0,
        loadingStats: false
      });
    }
  };

  if (loading) {
    return <div>Carregando...</div>;
  }

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Dashboard do Professor</h4>
          <p className="mb-0">Bem-vindo(a), {user?.name}</p>
        </div>
      </div>

      {/* Informação da Escola Selecionada */}
      {selectedSchool && getSelectedSchoolData() && (
        <Card border="light" className="shadow-sm mb-4">
          <Card.Body>
            <div className="d-flex align-items-center">
              <div className="icon icon-shape icon-md icon-shape-primary rounded me-3">
                <FontAwesomeIcon icon={faSchool} />
              </div>
              <div>
                <h5 className="mb-0">{getSelectedSchoolData().name}</h5>
                <small className="text-muted">Escola Atual</small>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Cards de Estatísticas */}
      <Row className="mb-4">
        <Col xs={12} sm={6} xl={4} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <Row className="d-block d-xl-flex align-items-center">
                <Col xl={5} className="text-xl-center d-flex align-items-center justify-content-xl-center mb-3 mb-xl-0">
                  <div className="icon icon-shape icon-md icon-shape-primary rounded me-4 me-sm-0">
                    <FontAwesomeIcon icon={faChalkboardTeacher} size="lg" />
                  </div>
                </Col>
                <Col xs={12} xl={7} className="px-xl-0">
                  <div className="d-none d-sm-block">
                    <h5 className="text-muted mb-1">Minhas Turmas</h5>
                    {stats.loadingStats ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <h3 className="mb-0">{stats.classes}</h3>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} xl={4} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <Row className="d-block d-xl-flex align-items-center">
                <Col xl={5} className="text-xl-center d-flex align-items-center justify-content-xl-center mb-3 mb-xl-0">
                  <div className="icon icon-shape icon-md icon-shape-secondary rounded me-4 me-sm-0">
                    <FontAwesomeIcon icon={faBook} size="lg" />
                  </div>
                </Col>
                <Col xs={12} xl={7} className="px-xl-0">
                  <div className="d-none d-sm-block">
                    <h5 className="text-muted mb-1">Disciplinas</h5>
                    {stats.loadingStats ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <h3 className="mb-0">{stats.subjects}</h3>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} xl={4} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <Row className="d-block d-xl-flex align-items-center">
                <Col xl={5} className="text-xl-center d-flex align-items-center justify-content-xl-center mb-3 mb-xl-0">
                  <div className="icon icon-shape icon-md icon-shape-tertiary rounded me-4 me-sm-0">
                    <FontAwesomeIcon icon={faUserGraduate} size="lg" />
                  </div>
                </Col>
                <Col xs={12} xl={7} className="px-xl-0">
                  <div className="d-none d-sm-block">
                    <h5 className="text-muted mb-1">Alunos</h5>
                    {stats.loadingStats ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <h3 className="mb-0">{stats.students}</h3>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Lista de Todas as Escolas */}
      {availableSchools.length > 1 && (
        <Row>
          <Col xs={12} className="mb-4">
            <Card border="light" className="shadow-sm">
              <Card.Header>
                <h5 className="mb-0">Minhas Escolas</h5>
              </Card.Header>
              <Card.Body>
                <Table responsive className="align-items-center table-flush">
                  <thead className="thead-light">
                    <tr>
                      <th scope="col">Escola</th>
                      <th scope="col">Função</th>
                      <th scope="col">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schools.length > 0 ? (
                      schools.map((schoolUser, index) => (
                        <tr key={index}>
                          <td className="fw-bold">{schoolUser.school?.name || 'Carregando...'}</td>
                          <td>
                            <Badge bg="info">
                              {schoolUser.role_display}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg="success" className="badge-lg">
                              Ativo
                            </Badge>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center">
                          Nenhuma escola encontrada
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default TeacherDashboard;
